import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { usePersonEditData } from '@/features/student-management/hooks/usePersonEditData';
import { useUpdatePersonByStep } from '@/features/student-management/hooks/useUpdatePersonByStep';
import { PersonForm } from '@/features/student-management/ui/organisms/PersonForm';
import { PersonEditTemplate } from '@/features/student-management/ui/templates/PersonEditTemplate';
import { Spinner } from '@/shared/ui/atoms';

const stepKeySchema = z.enum([
  'personal',
  'contact',
  'professional',
  'documents',
  'addresses',
  'profiles',
  'financial',
  'roles',
]);

export const Route = createFileRoute('/_authenticated/persons/$id/')({
  validateSearch: z.object({
    tab: stepKeySchema.optional(),
  }),
  component: PersonEditPage,
});

function PersonEditPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation('personManagement');

  const personId = Number(id);
  const { person, isLoading, isError, isLoadingProfiles, isLoadingFinancial } =
    usePersonEditData(personId);
  const updatePerson = useUpdatePersonByStep();

  if (isLoading) {
    return (
      <PersonEditTemplate title={t('breadcrumb.edit')}>
        <div className="flex justify-center py-lg">
          <Spinner />
        </div>
      </PersonEditTemplate>
    );
  }

  if (isError || !person) {
    return (
      <PersonEditTemplate title={t('breadcrumb.edit')}>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">{t('errors.notFound')}</p>
      </PersonEditTemplate>
    );
  }

  return (
    <PersonEditTemplate title={`${t('breadcrumb.edit')} — ${person.firstName} ${person.lastName}`}>
      <PersonForm
        person={person}
        isSubmitting={updatePerson.isPending}
        isLoadingProfiles={isLoadingProfiles}
        isLoadingFinancial={isLoadingFinancial}
        initialStep={tab}
        onSubmit={() => undefined}
        onSaveStep={({ step, values, dirtyFields }) =>
          updatePerson.submitStep(step, personId, values, dirtyFields)
        }
        onUpdateByStep={({ values, dirtyFields }) => {
          void updatePerson
            .submitChangedSections(personId, values, dirtyFields)
            .then(() => navigate({ to: '/persons' }));
        }}
        onCancel={() => void navigate({ to: '/persons' })}
      />
    </PersonEditTemplate>
  );
}
